import {
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject, takeUntil } from 'rxjs';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  Form,
  Field,
  Option,
} from '../../../../domain/form.interface';
import { Store, select } from '@ngrx/store';
import { setSelectedForm } from '../../../../infrastructure/store/selectors/form.selector';
import { setSelectedVersion } from '../../../../infrastructure/store/selectors/version.selector';
import { slugAsyncValidator } from '../../../../infrastructure/libs/slug.validator';
// Actions
import {
  editForm,
} from '../../../../infrastructure/store/actions/form.actions';
import { editVersion } from 'projects/forms/src/app/infrastructure/store/actions/version.actions';

@Component({
  selector: 'app-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
})
export class ModalComponent implements OnInit, OnDestroy {
  unsubscribe$ = new Subject<void>();

  form!: FormGroup;
  formOptions!: FormGroup;
  formSearchOptions!: FormGroup;
  formLevelLabels!: FormGroup;

  elementSelected!: Form;
  formSelected!: Form;
  versionSelected!: Form;

  title: string | null = null;
  index: number;

  typeHTML = [
    { name: 'TinyMCE', value: 'tiny' },
    { name: 'Search', value: 'search' },
    { name: 'Camera', value: 'webcam-image' },
    { name: 'Checkbox', value: 'checkbox' },
    { name: 'Date', value: 'date' },
    { name: 'Date/Time', value: 'date-time' },
    { name: 'File Uploader', value: 'uploader' },
    { name: 'File Multi Uploader', value: 'multi-uploader' },
    { name: 'Number', value: 'number' },
    { name: 'Paragraph', value: 'paragraph' },
    { name: 'Range', value: 'range' },
    { name: 'Select', value: 'select' },
    { name: 'Select Multiple', value: 'select-multiple' },
    { name: 'Select Multiple Flat Tree', value: 'select-flat-tree' },
    { name: 'Dropdown Cascade', value: 'dropdown-cascade' },
    { name: 'Tree Select', value: 'tree-select' },
    { name: 'Separator', value: 'separator' },
    { name: 'Signature', value: 'signature' },
    { name: 'Text Input', value: 'text' },
    { name: 'Time', value: 'time' },
    { name: 'Title', value: 'title' },
    { name: 'Radio Button', value: 'radio' },
    { name: 'Address', value: 'address' },
    { name: 'Phone number', value: 'phone-number' },
    { name: 'Email', value: 'email' },
  ];
  selectedType: string | null = null;

  selectedForm!: Form;
  update: boolean = false;
  field: Field = {} as Field;

  /*SHOW ELEMENTS*/
  showText: boolean = false;
  showLabel: boolean = false;
  showPlaceholder: boolean = false;
  showHelpText: boolean = false;
  showOptions: boolean = false;
  showOptionsCache: boolean = false;
  showCascadeOptions: boolean = false;
  showMax: boolean = false;
  showMin: boolean = false;
  showStep: boolean = false;
  showSearch: boolean = false;
  showDate: boolean = false;
  showVariable: boolean = false;
  showVariableNames: boolean = false;
  showEncrypted: boolean = false;
  showRequired: boolean = false;
  showHidden: boolean = false;
  showIndexable: boolean = false;
  showInmutable: boolean = false;
  showLocked: boolean = false;
  showSettings: boolean = false;

  // Visible when editor
  vwRules: { field: string; op: string; values: any[]; text: string }[] = [];
  vwJoin: 'all' | 'any' = 'all';
  vwAdvanced = false;
  vwError: string = null;
  vwFieldList: { name: string; label: string; options: any[] }[] = [];
  vwJoinOptions = [
    { value: 'all', label: 'all' },
    { value: 'any', label: 'any' },
  ];
  vwOperators = [
    { value: 'in', label: 'is one of' },
    { value: 'not_in', label: 'is not one of' },
    { value: 'match', label: 'contains' },
    { value: 'not_empty', label: 'has a value' },
    { value: 'empty', label: 'is empty' },
  ];
  showValidate: boolean = false;
  showMultiUploaderConcurrent: boolean = false;
  showMultiUploaderMaxFiles: boolean = false;
  showMultiUploaderTypeFiles: boolean = false;
  showMultiUploaderMaxSize: boolean = false;
  showCascadeLabels: boolean = false;

  selectedOptions: Array<Option> = [];
  selectedSearchOptions: Array<Option> = [];

  cascadeOptions: any[] = [];
  levelLabels: string[] = [];
  currentParentPath: number[] = [];

  // ============================
  // NEW: select-flat-tree builder
  // ============================
  flatTreeObject: Record<string, any> = {};
  flatTreePreview: string[] = [];

  constructor(
    public bsModalRef: BsModalRef,
    private fb: FormBuilder,
    private store: Store
  ) {
    this.form = this.fb.group({
      type: [null, [Validators.required]],
      name: [
        '',
        [Validators.required],
        slugAsyncValidator(() => this.getOtherFieldNamesForValidation()),
      ],
      label: [],
      text: [],
      placeholder: [],
      help_text: [],
      options: [],
      options_cache: [],
      options_object: [],
      level_labels: [],
      options_url: [],
      options_headers: [],
      options_key: [],
      options_value: [],
      search_url: [],
      search_query_param: [],
      search_headers: [],
      search_options: [],
      max: [],
      min: [],
      min_days_before_today: [],
      min_days_after_today: [],
      step: [],
      variable: [],
      variable_names: [],
      encrypted: [],
      required: [],
      hidden: [],
      inmutable: [],
      locked: [],
      indexable: [],
      validate: [],
      concurrent_uploads_multi: [],
      max_files_uploads_multi: [],
      max_size_uploads_multi: [],
      type_files_uploads_multi: [],
      multi_select: [],
      flat_tree_path: [],
      visible_when: [null, [this.visibleWhenValidation()]],
      clear_when_hidden: [],
    });

    this.formOptions = this.fb.group(
      {
        value_option: [null],
        label_option: [null],
      },
      {
        validators: [this.equalValueOptionValidation()],
      }
    );

    this.formSearchOptions = this.fb.group(
      {
        value_option: [null],
        label_option: [null],
      },
      {
        validators: [this.equalSearchValueOptionValidation()],
      }
    );

    this.formLevelLabels = this.fb.group({
      label_level: [null],
    });

    // RESET when type changes
    this.type?.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.formOptions.reset();
        this.formSearchOptions.reset();
        this.loadVisibleWhen(null);

        this.form.patchValue({
          name: null,
          label: null,
          text: null,
          placeholder: null,
          help_text: null,
          search_url: null,
          search_query_param: null,
          search_headers: null,
          search_options: null,
          options: null,
          options_cache: null,
          options_object: null,
          options_url: null,
          options_headers: null,
          options_key: null,
          options_value: null,
          max: null,
          min: null,
          min_days_before_today: null,
          min_days_after_today: null,
          step: null,
          variable: null,
          hidden: null,
          indexable: null,
          variable_names: null,
          encrypted: null,
          required: null,
          inmutable: null,
          locked: null,
          validate: null,
          concurrent_uploads_multi: null,
          max_files_uploads_multi: null,
          max_size_uploads_multi: null,
          visible_when: null,
          clear_when_hidden: null,
          type_files_uploads_multi: null,
          multi_select: null,
          flat_tree_path: null,
        });

        this.selectedOptions = [];
        this.selectedSearchOptions = [];
        this.cascadeOptions = [];
        this.currentParentPath = [];
        this.levelLabels = [];
        this.syncLevelLabelsToForm();

        // NEW: clear flat-tree builder state
        this.flatTreeObject = {};
        this.flatTreePreview = [];
      });
  }

  ngOnInit() {
    this.fillFormUpdate();

    this.store
      .pipe(select(setSelectedForm))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (v) => {
          this.formSelected = v;
          if (v) this.elementSelected = v;
          // Field names for the visible_when editor
          this.buildVwFieldList();
        },
        error: (e) => console.log('ERROR->', <any>e),
      });

    this.store
      .pipe(select(setSelectedVersion))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (v) => {
          this.versionSelected = v;
          if (v) this.elementSelected = v;
          // Field names for the visible_when editor
          this.buildVwFieldList();
        },
        error: (e) => console.log('ERROR->', <any>e),
      });
  }

  fillFormUpdate() {
    if (this.update) {
      console.log('FIELD-->', this.field);
      let data = { value: this.field.type };
      this.changeType(data);

      this.form.patchValue({
        type: this.field.type,
        name: this.field.name,
        label: this.field.label,
        text: this.field.text,
        placeholder: this.field.placeholder,
        help_text: this.field.help_text,
        options: this.field.options,
        options_cache: this.field.options_cache,
        options_object: this.field.options_object,
        level_labels: this.field.level_labels,
        options_url: this.field.options_url,
        options_headers: this.field.options_headers,
        options_key: this.field.options_key,
        options_value: this.field.options_value,
        search_url: this.field.search?.url,
        search_query_param: this.field.search?.query_param,
        search_headers: this.field.search?.headers,
        search_options: this.field.search?.options,
        max: this.field.max,
        min: this.field.min,
        min_days_before_today: this.field.min_days_before_today,
        min_days_after_today: this.field.min_days_after_today,
        step: this.field.step,
        encrypted: this.field.encrypted,
        required: this.field.required,
        inmutable: this.field.inmutable,
        locked: this.field.locked,
        variable: this.field.variable,
        hidden: this.field.hidden,
        indexable: this.field.indexable,
        variable_names: this.field.variable_names,
        validate: this.field.validate,
        concurrent_uploads_multi: this.field.concurrent_uploads_multi,
        max_files_uploads_multi: this.field.max_files_uploads_multi,
        max_size_uploads_multi: this.field.max_size_uploads_multi,
        type_files_uploads_multi: this.field.type_files_uploads_multi,
        multi_select: this.field.multi_select,
        flat_tree_path: this.field.flat_tree_path,
        visible_when: this.field.visible_when
          ? JSON.stringify(this.field.visible_when, null, 2)
          : null,
        clear_when_hidden: this.field.clear_when_hidden
      });
      this.loadVisibleWhen(this.field.visible_when);

      this.selectedOptions =
        this.options.value?.length != 0 ? this.options.value : [];
      this.selectedSearchOptions =
        this.search_options.value?.length != 0 ? this.search_options.value : [];

      this.levelLabels = Array.isArray(this.field.level_labels)
        ? [...this.field.level_labels]
        : [];
      this.syncLevelLabelsToForm();

      // Load cascade options if it's a dropdown-cascade or tree-select with nested options
      if (
        (this.field.type === 'dropdown-cascade' || this.field.type === 'tree-select') &&
        this.field.options &&
        Array.isArray(this.field.options)
      ) {
        const hasNestedStructure = this.field.options.some(
          (opt: any) => opt.children !== undefined
        );
        if (hasNestedStructure) {
          this.cascadeOptions = JSON.parse(JSON.stringify(this.field.options));
        }
      }

      // NEW: Load select-flat-tree object options (non-cache)
      if (
        this.field.type === 'select-flat-tree' &&
        !this.field.options_cache &&
        this.field.options &&
        typeof this.field.options === 'object' &&
        !Array.isArray(this.field.options)
      ) {
        this.flatTreeObject = JSON.parse(JSON.stringify(this.field.options));
        this.flatTreePreview = this.flattenTreeToPreview(this.flatTreeObject).sort();

        // ensure form.options is in sync
        this.form.patchValue({ options: this.flatTreeObject });
      }
    }
  }

  // -----------------------
  // GETTERS
  // -----------------------
  get type() {
    return this.form.get('type');
  }
  get name() {
    return this.form.get('name');
  }
  get label() {
    return this.form.get('label');
  }
  get text() {
    return this.form.get('text');
  }
  get placeholder() {
    return this.form.get('placeholder');
  }
  get textHelp() {
    return this.form.get('help_text');
  }
  get options() {
    return this.form.get('options');
  }
  get options_cache() {
    return this.form.get('options_cache');
  }
  get options_object() {
    return this.form.get('options_object');
  }
  get options_url() {
    return this.form.get('options_url');
  }
  get options_headers() {
    return this.form.get('options_headers');
  }
  get options_key() {
    return this.form.get('options_key');
  }
  get options_value() {
    return this.form.get('options_value');
  }
  get search_url() {
    return this.form.get('search_url');
  }
  get search_query_param() {
    return this.form.get('search_query_param');
  }
  get search_headers() {
    return this.form.get('search_headers');
  }
  get search_options() {
    return this.form.get('search_options');
  }
  get min() {
    return this.form.get('min');
  }
  get max() {
    return this.form.get('max');
  }
  get step() {
    return this.form.get('step');
  }
  get min_days_before_today() {
    return this.form.get('min_days_before_today');
  }
  get min_days_after_today() {
    return this.form.get('min_days_after_today');
  }
  get variable() {
    return this.form.get('variable');
  }
  get variableNames() {
    return this.form.get('variable_names');
  }
  get encrypted() {
    return this.form.get('encrypted');
  }
  get required() {
    return this.form.get('required');
  }
  get hidden() {
    return this.form.get('hidden');
  }
  get indexable() {
    return this.form.get('indexable');
  }
  get immutable() {
    return this.form.get('inmutable');
  }
  get locked() {
    return this.form.get('locked');
  }
  get valueOption() {
    return this.formOptions.get('value_option');
  }
  get labelOption() {
    return this.formOptions.get('label_option');
  }
  get valueSearchOption() {
    return this.formSearchOptions.get('value_option');
  }
  get labelSearchOption() {
    return this.formSearchOptions.get('label_option');
  }
  get validate() {
    return this.formOptions.get('validate');
  }
  get concurrentUploadsMulti() {
    return this.form.get('concurrent_uploads_multi');
  }
  get maxFilesUploadsMulti() {
    return this.form.get('max_files_uploads_multi');
  }
  get typeFilesUploadMulti() {
    return this.form.get('type_files_uploads_multi');
  }
  get maxSizeUploadsMulti() {
    return this.form.get('max_size_uploads_multi');
  }

  get flat_tree_path() {
    return this.form.get('flat_tree_path');
  }

  get flatTreePathInvalid(): boolean {
    const v = (this.flat_tree_path?.value || '').trim();
    return (
      (this.flat_tree_path?.dirty || this.flat_tree_path?.touched) &&
      v.length === 0
    );
  }

  private getOtherFieldNamesForValidation(): string[] {
    const fields = Array.isArray(this.elementSelected?.fields)
      ? this.elementSelected.fields
      : [];

    const currentFieldId = this.update ? this.field?.id : null;

    return fields
      .filter((item: Field | null | undefined): item is Field => !!item)
      .filter((item) => !currentFieldId || item.id !== currentFieldId)
      .map((item) => (item.name || '').toString().trim())
      .filter((name) => !!name.length);
  }

  // -----------------------
  // SAVE
  // -----------------------
  save(data: any) {
    this.form.markAllAsTouched();
    if (this.form.valid && this.formOptions.valid) {
      this.bsModalRef.hide();
      let tempField = { ...data };

      // visible_when: text in the modal, object in the field
      const visibleWhen = this.parseVisibleWhen(tempField.visible_when);
      if (visibleWhen) {
        tempField.visible_when = visibleWhen;
      } else {
        delete tempField.visible_when;
      }
      if (!tempField.clear_when_hidden) {
        delete tempField.clear_when_hidden;
      }

      console.log('DATA ->', { ...data });

      tempField.search = tempField.search || {};
      tempField.search.url = tempField.search_url || null;
      tempField.search.query_param = tempField.search_query_param || null;
      tempField.search.headers = tempField.search_headers || null;
      tempField.search.options = tempField.search_options || [];

      delete tempField.search_url;
      delete tempField.search_query_param;
      delete tempField.search_headers;
      delete tempField.search_options;

      // NEW: Save select-flat-tree options object when NOT using cache
      if (tempField.type === 'select-flat-tree' && !tempField.options_cache) {
        tempField.options = JSON.parse(JSON.stringify(this.flatTreeObject || {}));
      }

      // Save cascade options if dropdown-cascade or tree-select
      if (
        (tempField.type === 'dropdown-cascade' || tempField.type === 'tree-select') &&
        this.cascadeOptions.length > 0
      ) {
        tempField.options = this.cascadeOptions;
        console.log('=== SAVING CASCADE FIELD ===');
        console.log('Field type:', tempField.type);
        console.log('options_object:', tempField.options_object);
        console.log('options_cache:', tempField.options_cache);
        console.log('multi_select:', tempField.multi_select);
        console.log('CASCADE OPTIONS being saved:', JSON.stringify(this.cascadeOptions, null, 2));
        console.log('============================');
      }

      if (tempField.type === 'dropdown-cascade') {
        tempField.level_labels = [...this.levelLabels];
      }

      if (this.update) {
        // UPDATE FIELD
        if (this.elementSelected.pk == 'form') {
          tempField['id'] = this.field.id;
          let tempArray = [...this.elementSelected.fields];
          tempArray[this.index] = tempField;
          let tempElement = { ...this.elementSelected };

          console.log('FIELD UPDATE -->', tempField);

          // Update field in layout
          let updatedLayoutElement = this.findAndReplaceObjects(
            tempElement,
            tempField,
            tempField.id
          );

          this.store.dispatch(
            editForm({
              form: {
                ...updatedLayoutElement,
                fields: tempArray,
              },
            })
          );
        }
      } else {
        // CREATE FIELD
        tempField['id'] = this.generateUniqueId();
        let tempArray = [...this.elementSelected.fields];
        tempArray = [tempField, ...tempArray];

        console.log('FIELD CREATE -->', tempField);

        if (this.elementSelected.pk == 'form') {
          this.store.dispatch(
            editForm({
              form: {
                ...this.elementSelected,
                fields: tempArray,
              },
            })
          );
        } else {
          this.store.dispatch(
            editVersion({
              formId: this.elementSelected.form_id as string,
              version: {
                ...this.elementSelected,
                fields: tempArray,
              },
            })
          );
        }
      }
    }
  }

  cancel() {
    this.form.reset();
    this.formOptions.reset();
    this.bsModalRef?.hide();
  }

  // -----------------------
  // Cascade Options Methods
  // -----------------------
  addCascadeOption() {
    const value = this.formOptions.get('value_option')?.value?.trim();
    const label = this.formOptions.get('label_option')?.value?.trim();

    if (!value || !label) return;

    const newOption = {
      value_option: value,
      label_option: label,
      children: [],
    };

    if (this.currentParentPath.length === 0) {
      this.cascadeOptions.push(newOption);
    } else {
      const parent = this.getOptionByPath(this.currentParentPath);
      if (parent) {
        if (!parent.children) parent.children = [];
        parent.children.push(newOption);
      }
    }

    this.syncCascadeOptionsToForm();

    this.formOptions.patchValue({
      value_option: '',
      label_option: '',
    });
  }

  navigateToChildren(path: number[]) {
    this.currentParentPath = [...path];
  }

  navigateToParent() {
    if (this.currentParentPath.length > 0) {
      this.currentParentPath.pop();
    }
  }

  getCurrentLevelOptions(): any[] {
    if (this.currentParentPath.length === 0) return this.cascadeOptions;
    const parent = this.getOptionByPath(this.currentParentPath);
    return parent ? parent.children || [] : [];
  }

  getOptionByPath(path: number[]): any {
    let current = this.cascadeOptions;
    let option: any = null;

    for (let i = 0; i < path.length; i++) {
      const index = path[i];
      if (current && current[index]) {
        option = current[index];
        current = option.children;
      } else {
        return null;
      }
    }
    return option;
  }

  deleteCascadeOption(index: number) {
    const currentLevel = this.getCurrentLevelOptions();
    currentLevel.splice(index, 1);
    this.syncCascadeOptionsToForm();
  }

  addLevelLabel(): void {
    const label = this.formLevelLabels.get('label_level')?.value?.trim();
    if (!label) return;
    this.levelLabels = [...this.levelLabels, label];
    this.syncLevelLabelsToForm();
    this.formLevelLabels.patchValue({ label_level: '' });
  }

  deleteLevelLabel(index: number): void {
    if (index < 0 || index >= this.levelLabels.length) return;
    this.levelLabels = this.levelLabels.filter((_, i) => i !== index);
    this.syncLevelLabelsToForm();
  }

  private syncLevelLabelsToForm(): void {
    this.form.patchValue({
      level_labels: [...this.levelLabels],
    });
  }

  private syncCascadeOptionsToForm(): void {
    this.form.patchValue({
      options: JSON.parse(JSON.stringify(this.cascadeOptions)),
    });
  }

  getCurrentPathLabel(): string {
    if (this.currentParentPath.length === 0) return 'Root level';

    const labels: string[] = [];
    let current = this.cascadeOptions;

    for (let i = 0; i < this.currentParentPath.length; i++) {
      const index = this.currentParentPath[i];
      if (current && current[index]) {
        labels.push(current[index].label_option);
        current = current[index].children;
      }
    }
    return labels.join(' > ');
  }

  getChildPath(index: number): number[] {
    return [...this.currentParentPath, index];
  }

  hasChildren(option: any): boolean {
    return option.children && option.children.length > 0;
  }

  getChildrenCount(option: any): number {
    return option.children ? option.children.length : 0;
  }

  isAtRootLevel(): boolean {
    return this.currentParentPath.length === 0;
  }

  hasRootOptions(): boolean {
    return this.cascadeOptions.length > 0;
  }

  getCurrentLevelEmpty(): boolean {
    return this.getCurrentLevelOptions().length === 0;
  }

  getEmptyMessage(): string {
    return this.currentParentPath.length > 0
      ? 'No child options. Add one using the form above.'
      : 'No options available. Add the first option.';
  }

  getAddButtonLabel(): string {
    return this.currentParentPath.length > 0 ? 'Add child' : 'Add option';
  }

  canAddOption(): boolean {
    const value = this.formOptions.get('value_option')?.value;
    const label = this.formOptions.get('label_option')?.value;
    return !!(value && label);
  }

  // -----------------------
  // NEW: Flat Tree Builder Methods
  // -----------------------
  private normalizeFlatPath(input: string): string[] {
    return (input || '')
      .split('/')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  private flatPathToString(path: string[]): string {
    return path.join('/');
  }

  private existsFlatPath(path: string[]): boolean {
    const target = this.flatPathToString(path).toLowerCase();
    return this.flatTreePreview.some((p) => p.toLowerCase() === target);
  }

  addFlatTreePath(): void {
    const raw = (this.flat_tree_path?.value || '').trim();
    const path = this.normalizeFlatPath(raw);

    if (!path.length) {
      this.flat_tree_path?.markAsTouched();
      return;
    }

    if (this.existsFlatPath(path)) {
      this.form.setErrors({ ...(this.form.errors || {}), flatTreeDuplicate: true });
      return;
    }

    // clear duplicate error if present
    if (this.form.errors?.flatTreeDuplicate) {
      const { flatTreeDuplicate, ...rest } = this.form.errors || {};
      this.form.setErrors(Object.keys(rest).length ? rest : null);
    }

    // insert into tree object
    this.insertPathIntoTree(this.flatTreeObject, path);

    // update preview list
    this.flatTreePreview = [...this.flatTreePreview, this.flatPathToString(path)].sort();

    // sync into form.options
    this.form.patchValue({
      options: this.flatTreeObject,
    });

    // clear input
    this.form.patchValue({ flat_tree_path: '' });
    this.flat_tree_path?.markAsPristine();
    this.flat_tree_path?.markAsUntouched();
  }

  deleteFlatTreePath(index: number): void {
    if (index < 0 || index >= this.flatTreePreview.length) return;

    const copy = [...this.flatTreePreview];
    copy.splice(index, 1);
    this.flatTreePreview = copy;

    // rebuild tree from preview for safety
    const rebuilt: Record<string, any> = {};
    for (const p of this.flatTreePreview) {
      const path = this.normalizeFlatPath(p);
      this.insertPathIntoTree(rebuilt, path);
    }
    this.flatTreeObject = rebuilt;

    this.form.patchValue({
      options: this.flatTreeObject,
    });
  }

  private insertPathIntoTree(root: Record<string, any>, path: string[]): void {
    if (!path.length) return;

    const [padre, ...rest] = path;
    if (!padre) return;

    if (!root[padre]) root[padre] = [];
    root[padre] = this.insertIntoChildrenArray(root[padre], rest);
  }

  private insertIntoChildrenArray(children: any, rest: string[]): any[] {
    let arr: any[] = Array.isArray(children) ? children : [];
    if (!rest.length) return arr;

    const [token, ...tail] = rest;
    if (!token) return arr;

    // Leaf
    if (!tail.length) {
      if (!arr.some((x) => typeof x === 'string' && x === token)) {
        arr = [...arr, token];
      }
      return arr;
    }

    // Branch
    const idx = arr.findIndex(
      (x) =>
        typeof x === 'object' &&
        x !== null &&
        !Array.isArray(x) &&
        Object.prototype.hasOwnProperty.call(x, token)
    );

    if (idx >= 0) {
      const existing = arr[idx];
      const updated = {
        ...existing,
        [token]: this.insertIntoChildrenArray(existing[token], tail),
      };
      arr = arr.map((x, i) => (i === idx ? updated : x));
      return arr;
    }

    const newBranch = { [token]: this.insertIntoChildrenArray([], tail) };
    arr = [...arr, newBranch];
    return arr;
  }

  private flattenTreeToPreview(tree: Record<string, any>): string[] {
    const out: string[] = [];

    const walk = (node: any, prefix: string[]) => {
      if (node === null || node === undefined) return;

      if (typeof node === 'string') {
        out.push([...prefix, node].join('/'));
        return;
      }

      if (Array.isArray(node)) {
        node.forEach((child) => walk(child, prefix));
        return;
      }

      if (typeof node === 'object') {
        Object.entries(node).forEach(([k, v]) => walk(v, [...prefix, k]));
      }
    };

    Object.entries(tree).forEach(([k, v]) => walk(v, [k]));
    return out;
  }

  // -----------------------
  // OPTIONS (standard)
  // -----------------------
  toArray(str: string) {
    let text = str.split(/\s+/).join('');
    return text.length != 0 ? text.replace(/\s+/g, '').split(',') : null;
  }

  newOption(data: any) {
    this.formOptions.markAllAsTouched();
    if (this.formOptions.valid) {
      let tempOption = { ...data };
      tempOption.value_option = this.formOptions.get('value_option')?.value;
      tempOption.label_option = this.formOptions.get('label_option')?.value;

      if (!this.selectedOptions) this.selectedOptions = [];

      this.selectedOptions = [...this.selectedOptions, tempOption];
      this.formOptions.reset();
      this.form.patchValue({
        options: this.selectedOptions,
      });
    }
  }

  deleteOption(index: number): void {
    if (index >= 0 && index < this.selectedOptions.length) {
      this.selectedOptions = this.selectedOptions.filter((_, i) => i !== index);
      this.form.patchValue({
        options: [...this.selectedOptions],
      });
    }
  }

  // -----------------------
  // VISIBLE WHEN EDITOR
  // -----------------------
  private vwOps = ['in', 'not_in', 'match', 'empty', 'not_empty'];

  // Other fields of the form that a condition can use
  buildVwFieldList() {
    const fields = Array.isArray(this.elementSelected?.fields)
      ? this.elementSelected.fields
      : [];
    const currentName = this.update ? this.field?.name : null;
    this.vwFieldList = fields
      .filter((f: any) => f?.name && f.name !== currentName)
      .filter((f: any) => !['title', 'paragraph', 'separator'].includes(f.type))
      .map((f: any) => {
        const hasOptions =
          Array.isArray(f.options) &&
          f.options.length > 0 &&
          f.options[0]?.value_option !== undefined;
        return {
          name: f.name,
          label: f.label ? `${f.label} (${f.name})` : f.name,
          options: hasOptions ? f.options : [],
        };
      });
  }

  // One condition like {"field": "x", "in": [...]}
  private isSimpleCond(c: any): boolean {
    if (!c || typeof c !== 'object' || Array.isArray(c) || !c.field) return false;
    const keys = Object.keys(c).filter((k) => k !== 'field');
    return keys.length === 1 && this.vwOps.includes(keys[0]);
  }

  private condToRule(c: any) {
    const op = Object.keys(c).find((k) => k !== 'field');
    const value = c[op];
    return {
      field: c.field,
      op,
      values: Array.isArray(value) ? [...value] : [],
      text: op === 'match' ? String(value ?? '') : Array.isArray(value) ? value.join(', ') : '',
    };
  }

  private ruleToCond(r: any): any {
    if (r.op === 'empty' || r.op === 'not_empty') return { field: r.field, [r.op]: true };
    if (r.op === 'match') return { field: r.field, match: r.text || '' };
    const values = this.vwOptions(r.field).length
      ? r.values || []
      : String(r.text || '').split(',').map((v) => v.trim()).filter((v) => v);
    return { field: r.field, [r.op]: values };
  }

  // Fill the editor from a saved condition
  loadVisibleWhen(cond: any) {
    this.buildVwFieldList();
    this.vwError = null;
    this.vwRules = [];
    this.vwJoin = 'all';
    this.vwAdvanced = false;
    if (!cond) return;
    const list = Array.isArray(cond) ? cond : null;
    if (this.isSimpleCond(cond)) {
      this.vwRules = [this.condToRule(cond)];
    } else if (list && list.every((c) => this.isSimpleCond(c))) {
      this.vwRules = list.map((c) => this.condToRule(c));
    } else if (
      (Array.isArray(cond.all) || Array.isArray(cond.any)) &&
      Object.keys(cond).length === 1 &&
      (cond.all || cond.any).every((c: any) => this.isSimpleCond(c))
    ) {
      this.vwJoin = cond.all ? 'all' : 'any';
      this.vwRules = (cond.all || cond.any).map((c: any) => this.condToRule(c));
    } else {
      // Too complex for the simple editor
      this.vwAdvanced = true;
    }
  }

  // Write the editor rules into the form control
  syncVisibleWhen() {
    if (this.vwAdvanced) return;
    const conds = this.vwRules.filter((r) => r.field && r.op).map((r) => this.ruleToCond(r));
    let cond = null;
    if (conds.length === 1) cond = conds[0];
    if (conds.length > 1) cond = { [this.vwJoin]: conds };
    this.visible_when.setValue(cond ? JSON.stringify(cond, null, 2) : null);
    this.visible_when.markAsDirty();
  }

  addVwRule() {
    this.vwRules = [...this.vwRules, { field: null, op: 'in', values: [], text: '' }];
  }

  removeVwRule(index: number) {
    this.vwRules = this.vwRules.filter((_, i) => i !== index);
    this.syncVisibleWhen();
  }

  onVwFieldChange(rule: any) {
    rule.values = [];
    rule.text = '';
    this.syncVisibleWhen();
  }

  vwOptions(name: string): any[] {
    return this.vwFieldList.find((f) => f.name === name)?.options || [];
  }

  // Which value input a rule needs
  vwValueInput(rule: any): string {
    if (rule.op === 'empty' || rule.op === 'not_empty') return 'none';
    if (rule.op === 'match') return 'text';
    return this.vwOptions(rule.field).length ? 'options' : 'text';
  }

  toggleVwAdvanced() {
    this.vwError = null;
    if (!this.vwAdvanced) {
      this.syncVisibleWhen();
      this.vwAdvanced = true;
      return;
    }
    // Back to the simple editor only if the JSON fits it
    let cond = null;
    try {
      cond = this.parseVisibleWhen(this.visible_when.value);
    } catch {
      this.vwError = 'Fix the JSON first.';
      return;
    }
    this.loadVisibleWhen(cond);
    if (this.vwAdvanced) {
      this.vwError = 'This condition is too complex for the simple editor.';
    }
  }

  // Empty text or a JSON object
  parseVisibleWhen(text: any): any {
    if (text === null || text === undefined || String(text).trim() === '') {
      return null;
    }
    return JSON.parse(text);
  }

  visibleWhenValidation(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      try {
        const value = this.parseVisibleWhen(control.value);
        if (value === null) return null;
        if (typeof value !== 'object') return { visibleWhen: true };
        return null;
      } catch {
        return { visibleWhen: true };
      }
    };
  }

  get visible_when() {
    return this.form.get('visible_when');
  }

  equalValueOptionValidation(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.get('value_option')?.value != null) {
        let value = control.get('value_option')?.value;
        if (Array.isArray(this.selectedOptions)) {
          let temp = this.selectedOptions.filter((elem) => elem.value_option == value);
          const isValid = temp.length == 0;
          return isValid ? null : { equalValue: true };
        } else {
          console.error('selectedOptions is not an array or is undefined');
          return null;
        }
      }
      return null;
    };
  }

  newSearchOption(data: any) {
    this.formSearchOptions.markAllAsTouched();
    if (this.formSearchOptions.valid) {
      let tempOption = { ...data };
      tempOption.value_option = this.formSearchOptions.get('value_option')?.value;
      tempOption.label_option = this.formSearchOptions.get('label_option')?.value;

      this.selectedSearchOptions = [...this.selectedSearchOptions, tempOption];

      this.formSearchOptions.reset();
      this.form.patchValue({
        search_options: this.selectedSearchOptions,
      });
    }
  }

  deleteSearchOption(index: number): void {
    if (index >= 0 && index < this.selectedSearchOptions.length) {
      this.selectedSearchOptions = this.selectedSearchOptions.filter((_, i) => i !== index);
      this.form.patchValue({
        search_options: [...this.selectedSearchOptions],
      });
    }
  }

  equalSearchValueOptionValidation(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.get('value_option')?.value != null) {
        let value = control.get('value_option')?.value;
        let temp = this.selectedSearchOptions.filter((elem) => elem.value_option == value);
        const isValid = temp.length == 0;
        return isValid ? null : { equalValue: true };
      }
      return null;
    };
  }

  // -----------------------
  // TYPE UI toggles
  // -----------------------
  changeType(data: any) {
    this.selectedType = data.value;

    this.showCascadeOptions = false;
    this.showCascadeLabels = false;

    switch (data.value) {
      case 'tiny':
        this.showText = false;
        this.showLabel = true;
        this.showPlaceholder = false;
        this.showHelpText = true;
        this.showSearch = false;
        this.showOptions = false;
        this.showOptionsCache = false;
        this.showMax = false;
        this.showMin = false;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = true;
        this.showRequired = true;
        this.showInmutable = true;
        this.showLocked = true;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = true;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        break;

      case 'title':
        this.showText = true;
        this.showLabel = false;
        this.showPlaceholder = false;
        this.showHelpText = false;
        this.showSearch = false;
        this.showOptions = false;
        this.showOptionsCache = false;
        this.showMax = false;
        this.showMin = false;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = true;
        this.showVariableNames = true;
        this.showEncrypted = false;
        this.showRequired = false;
        this.showHidden = true;
        this.showIndexable = true;
        this.showInmutable = false;
        this.showLocked = false;
        this.showSettings = true;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        break;

      case 'paragraph':
        this.showText = true;
        this.showLabel = false;
        this.showPlaceholder = false;
        this.showHelpText = false;
        this.showSearch = false;
        this.showOptions = false;
        this.showOptionsCache = false;
        this.showMax = false;
        this.showMin = false;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = true;
        this.showVariableNames = true;
        this.showEncrypted = false;
        this.showRequired = false;
        this.showInmutable = false;
        this.showLocked = false;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = true;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        break;

      case 'text':
        this.showText = false;
        this.showLabel = true;
        this.showPlaceholder = true;
        this.showHelpText = true;
        this.showSearch = false;
        this.showOptions = false;
        this.showOptionsCache = false;
        this.showMax = false;
        this.showMin = false;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = true;
        this.showRequired = true;
        this.showInmutable = true;
        this.showLocked = true;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = true;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        break;

      case 'uploader':
        this.showText = false;
        this.showLabel = true;
        this.showPlaceholder = false;
        this.showHelpText = true;
        this.showSearch = false;
        this.showOptions = false;
        this.showOptionsCache = false;
        this.showMax = false;
        this.showMin = false;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = true;
        this.showRequired = true;
        this.showInmutable = true;
        this.showLocked = true;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = true;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        break;

      case 'multi-uploader':
        this.showText = false;
        this.showLabel = true;
        this.showPlaceholder = false;
        this.showHelpText = true;
        this.showSearch = false;
        this.showOptions = false;
        this.showOptionsCache = false;
        this.showMax = false;
        this.showMin = false;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = true;
        this.showRequired = true;
        this.showInmutable = true;
        this.showLocked = true;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = true;
        this.showMultiUploaderConcurrent = true;
        this.showMultiUploaderMaxFiles = true;
        this.showMultiUploaderTypeFiles = true;
        this.showMultiUploaderMaxSize = true;
        break;

      case 'select-flat-tree':
        this.showText = false;
        this.showLabel = true;
        this.showPlaceholder = true;
        this.showHelpText = true;
        this.showSearch = false;
        this.showOptions = true;
        this.showOptionsCache = true;
        this.showMax = false;
        this.showMin = false;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = true;
        this.showRequired = true;
        this.showInmutable = true;
        this.showLocked = true;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = true;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        break;

      case 'number':
        this.showText = false;
        this.showLabel = true;
        this.showPlaceholder = true;
        this.showHelpText = true;
        this.showSearch = false;
        this.showOptions = false;
        this.showOptionsCache = false;
        this.showMax = true;
        this.showMin = true;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = true;
        this.showRequired = true;
        this.showInmutable = true;
        this.showLocked = true;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = true;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        break;

      case 'range':
        this.showText = false;
        this.showLabel = true;
        this.showPlaceholder = false;
        this.showHelpText = true;
        this.showSearch = false;
        this.showOptions = false;
        this.showOptionsCache = false;
        this.showMax = true;
        this.showMin = true;
        this.showDate = false;
        this.showStep = true;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = true;
        this.showRequired = true;
        this.showInmutable = true;
        this.showLocked = true;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = true;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        break;

      case 'select':
        this.showText = false;
        this.showLabel = true;
        this.showPlaceholder = true;
        this.showHelpText = true;
        this.showSearch = false;
        this.showOptions = true;
        this.showOptionsCache = true;
        this.showMax = false;
        this.showMin = false;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = true;
        this.showRequired = true;
        this.showInmutable = true;
        this.showLocked = true;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = true;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        break;

      case 'checkbox':
        this.showText = false;
        this.showLabel = true;
        this.showPlaceholder = false;
        this.showHelpText = true;
        this.showSearch = false;
        this.showOptions = true;
        this.showOptionsCache = false;
        this.showMax = false;
        this.showMin = false;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = true;
        this.showRequired = true;
        this.showInmutable = true;
        this.showLocked = true;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = true;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        break;

      case 'radio':
        this.showText = false;
        this.showLabel = true;
        this.showPlaceholder = false;
        this.showHelpText = true;
        this.showSearch = false;
        this.showOptions = true;
        this.showOptionsCache = false;
        this.showMax = false;
        this.showMin = false;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = true;
        this.showRequired = true;
        this.showInmutable = true;
        this.showLocked = true;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = true;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        break;

      case 'date':
        this.showText = false;
        this.showLabel = true;
        this.showPlaceholder = false;
        this.showHelpText = true;
        this.showSearch = false;
        this.showOptions = false;
        this.showOptionsCache = false;
        this.showMax = false;
        this.showMin = false;
        this.showDate = true;
        this.showStep = false;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = true;
        this.showRequired = true;
        this.showInmutable = true;
        this.showLocked = true;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = true;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        break;

      case 'date-time':
        this.showText = false;
        this.showLabel = true;
        this.showPlaceholder = false;
        this.showHelpText = true;
        this.showSearch = false;
        this.showOptions = false;
        this.showOptionsCache = false;
        this.showMax = false;
        this.showMin = false;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = true;
        this.showRequired = true;
        this.showInmutable = true;
        this.showLocked = true;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = true;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        break;

      case 'time':
        this.showText = false;
        this.showLabel = true;
        this.showPlaceholder = false;
        this.showHelpText = true;
        this.showSearch = false;
        this.showOptions = false;
        this.showOptionsCache = false;
        this.showMax = false;
        this.showMin = false;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = true;
        this.showRequired = true;
        this.showLocked = true;
        this.showInmutable = true;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = true;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        break;

      case 'webcam-image':
        this.showText = false;
        this.showLabel = true;
        this.showPlaceholder = false;
        this.showHelpText = true;
        this.showSearch = false;
        this.showOptions = false;
        this.showOptionsCache = false;
        this.showMax = false;
        this.showMin = false;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = true;
        this.showRequired = true;
        this.showInmutable = true;
        this.showLocked = true;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = true;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        break;

      case 'signature':
        this.showText = false;
        this.showLabel = true;
        this.showPlaceholder = false;
        this.showHelpText = true;
        this.showSearch = false;
        this.showOptions = false;
        this.showOptionsCache = false;
        this.showMax = false;
        this.showMin = false;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = true;
        this.showRequired = true;
        this.showHidden = true;
        this.showIndexable = true;
        this.showLocked = true;
        this.showInmutable = true;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        break;

      case 'separator':
        this.showText = false;
        this.showLabel = false;
        this.showPlaceholder = false;
        this.showHelpText = false;
        this.showSearch = false;
        this.showOptions = false;
        this.showOptionsCache = false;
        this.showMax = false;
        this.showMin = false;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = false;
        this.showRequired = false;
        this.showInmutable = false;
        this.showLocked = false;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = false;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        break;

      case 'address':
        this.showText = false;
        this.showLabel = true;
        this.showPlaceholder = false;
        this.showHelpText = true;
        this.showSearch = false;
        this.showOptions = false;
        this.showOptionsCache = false;
        this.showMax = false;
        this.showMin = false;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = true;
        this.showRequired = true;
        this.showInmutable = true;
        this.showLocked = true;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = true;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        break;

      case 'phone-number':
        this.showText = false;
        this.showLabel = true;
        this.showPlaceholder = true;
        this.showHelpText = true;
        this.showSearch = false;
        this.showOptions = false;
        this.showOptionsCache = false;
        this.showMax = true;
        this.showMin = true;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = true;
        this.showRequired = true;
        this.showInmutable = true;
        this.showLocked = true;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = true;
        this.showValidate = true;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        break;

      case 'email':
        this.showText = false;
        this.showLabel = true;
        this.showPlaceholder = false;
        this.showHelpText = true;
        this.showSearch = false;
        this.showOptions = false;
        this.showOptionsCache = false;
        this.showMax = false;
        this.showMin = false;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = true;
        this.showRequired = true;
        this.showInmutable = true;
        this.showLocked = true;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = true;
        this.showValidate = true;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        break;

      case 'search':
        this.showText = false;
        this.showLabel = true;
        this.showPlaceholder = true;
        this.showHelpText = true;
        this.showSearch = true;
        this.showOptions = false;
        this.showOptionsCache = false;
        this.showMax = false;
        this.showMin = false;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = true;
        this.showRequired = true;
        this.showInmutable = true;
        this.showLocked = true;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = true;
        this.showValidate = true;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        break;

      case 'select-multiple':
        this.showText = false;
        this.showLabel = true;
        this.showPlaceholder = true;
        this.showHelpText = true;
        this.showSearch = false;
        this.showOptions = true;
        this.showOptionsCache = true;
        this.showMax = false;
        this.showMin = false;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = true;
        this.showRequired = true;
        this.showInmutable = true;
        this.showLocked = true;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = true;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        break;

      case 'tree-select':
        this.showText = false;
        this.showLabel = true;
        this.showPlaceholder = true;
        this.showHelpText = true;
        this.showSearch = false;
        this.showOptions = false;
        this.showOptionsCache = true;
        this.showCascadeOptions = true;
        this.showMax = false;
        this.showMin = false;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = true;
        this.showRequired = true;
        this.showInmutable = true;
        this.showLocked = true;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = true;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        this.showCascadeLabels = false;
        break;

      case 'dropdown-cascade':
        this.showText = false;
        this.showLabel = true;
        this.showPlaceholder = true;
        this.showHelpText = true;
        this.showSearch = false;
        this.showOptions = false;
        this.showOptionsCache = true;
        this.showCascadeOptions = true;
        this.showMax = false;
        this.showMin = false;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = true;
        this.showRequired = true;
        this.showInmutable = true;
        this.showLocked = true;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = true;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        this.showCascadeLabels = true;
        break;

      default:
        this.showText = false;
        this.showLabel = false;
        this.showPlaceholder = false;
        this.showHelpText = false;
        this.showSearch = false;
        this.showOptions = false;
        this.showOptionsCache = false;
        this.showMax = false;
        this.showMin = false;
        this.showDate = false;
        this.showStep = false;
        this.showVariable = false;
        this.showVariableNames = false;
        this.showEncrypted = false;
        this.showRequired = false;
        this.showInmutable = false;
        this.showLocked = false;
        this.showHidden = true;
        this.showIndexable = true;
        this.showSettings = false;
        this.showMultiUploaderConcurrent = false;
        this.showMultiUploaderMaxFiles = false;
        this.showMultiUploaderTypeFiles = false;
        this.showMultiUploaderMaxSize = false;
        break;
    }
  }

  onKeyPress(event: KeyboardEvent): void {
    if (!/^[a-zA-Z0-9_-]+$/.test(event.key)) {
      event.preventDefault();
    }
  }

  generateUniqueId(): string {
    const randomNumber: string = Math.floor(Math.random() * Date.now()).toString(16);
    const uniqueId: string = `${Date.now().toString(16)}${randomNumber}`;
    return uniqueId;
  }

  findAndReplaceObjects(
    originalObject: any,
    replacementObject: Field,
    objectId: string
  ): any {
    if (originalObject && typeof originalObject === 'object') {
      if (Array.isArray(originalObject)) {
        return originalObject.map((item) =>
          this.findAndReplaceObjects(item, replacementObject, objectId)
        );
      } else {
        const updatedObject: { [key: string]: any } = {};
        for (const key in originalObject) {
          if (originalObject.hasOwnProperty(key)) {
            updatedObject[key] = this.findAndReplaceObjects(
              originalObject[key],
              replacementObject,
              objectId
            );
          }
        }
        if (originalObject.id === objectId) {
          return replacementObject;
        }
        return updatedObject;
      }
    } else {
      return originalObject;
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}